import type { FindingOut } from '@/api/types';

/** A visual tag rendered as a colored pill on a finding card.
 *
 *  The shape is generic so future tag classes (province, supplier kind,
 *  regulatory class) can slot into the same `tagsFor()` pipeline without
 *  the renderer changing.
 */
export interface Tag {
  id: string;
  label: string;
  tooltip: string;
  emoji: string;
  className: string;
}

interface CurrencyTagMeta {
  emoji: string;
  className: string;
  country: string;
}

export const CURRENCY_TAG_META: Record<string, CurrencyTagMeta> = {
  CAD: { emoji: '🇨🇦', className: 'bg-red-100 text-red-800', country: 'Canadian dollar' },
  USD: { emoji: '🇺🇸', className: 'bg-blue-100 text-blue-800', country: 'United States dollar' },
  GBP: { emoji: '🇬🇧', className: 'bg-indigo-100 text-indigo-800', country: 'Pound sterling' },
  EUR: { emoji: '🇪🇺', className: 'bg-amber-100 text-amber-800', country: 'Euro' },
  AED: { emoji: '🇦🇪', className: 'bg-emerald-100 text-emerald-800', country: 'UAE dirham' },
};

const FOREIGN_FALLBACK: CurrencyTagMeta = {
  emoji: '🌐',
  className: 'bg-slate-100 text-slate-700',
  country: 'Foreign currency',
};

function currencyTag(finding: FindingOut): Tag | null {
  const code = finding.recovery?.currency?.toUpperCase() ?? null;
  if (code === null || code === '') return null;
  const meta = CURRENCY_TAG_META[code] ?? FOREIGN_FALLBACK;
  return {
    id: `currency:${code}`,
    label: code,
    tooltip: `${meta.country} (${code})`,
    emoji: meta.emoji,
    className: meta.className,
  };
}

/** Expected effective tax rates as decimals, keyed on the
 *  `evidence['supplier.province']` value the engine stamps. Province
 *  must appear in this map for the tag to render — anything missing
 *  hides the pill rather than emit a misleading signal.
 *
 *  Scope: only provinces where the bill's tax_amount cleanly equals
 *  the federal-or-harmonized rate. BC / SK / MB / QC are intentionally
 *  omitted — those provinces have separate PST / RST / QST that the
 *  supplier bundles into the tax_amount on a bill, so comparing the
 *  effective rate against a federal-only 5% baseline would misclassify
 *  every normal bill as severely over-charged. Restore them only after
 *  the ingest pipeline can split provincial-from-federal tax. */
const EXPECTED_RATE_BY_PROVINCE: Record<string, number> = {
  ON: 0.13,
  NS: 0.15,
  NB: 0.15,
  NL: 0.15,
  PE: 0.15,
  AB: 0.05,
  NT: 0.05,
  NU: 0.05,
  YT: 0.05,
};

const MIN_TXN_COUNT_FOR_RATE_TAG = 3;

function readNumberish(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function effectiveRateTag(finding: FindingOut): Tag | null {
  const ev = finding.evidence ?? {};
  const province = String(ev['supplier.province'] ?? '').trim().toUpperCase();
  const expected = EXPECTED_RATE_BY_PROVINCE[province];
  if (expected === undefined) return null;

  const txnCount = readNumberish(ev['supplier.txn_count_run']);
  if (txnCount === null || txnCount < MIN_TXN_COUNT_FOR_RATE_TAG) return null;

  const effective = readNumberish(ev['supplier.effective_rate_run']);
  if (effective === null) return null;

  const pct = (effective * 100).toFixed(1);
  const expectedPct = (expected * 100).toFixed(0);
  const tooltipBase = `Supplier effective rate ${pct}% across ${txnCount} txns on this run; expected ${expectedPct}% (${province})`;

  // Color bands per INP-383:
  //   green   — within ±1pt of expected (engine likely mis-fired)
  //   indigo  — over-charged by >1pt
  //   red     — effective ≤ half of expected (major rate gap, high recovery signal)
  //   amber   — partial gap in between
  const gap = expected - effective; // positive = under-charged
  let className: string;
  let stance: string;
  if (Math.abs(gap) <= 0.01) {
    className = 'bg-emerald-100 text-emerald-800';
    stance = 'effective ≈ expected — engine likely mis-fired';
  } else if (gap < 0) {
    className = 'bg-indigo-100 text-indigo-800';
    stance = 'over-charged — verify supplier registration';
  } else if (effective <= expected / 2) {
    className = 'bg-red-100 text-red-800';
    stance = 'major rate gap — high recovery signal';
  } else {
    className = 'bg-amber-100 text-amber-800';
    stance = 'partial rate gap — possible recovery';
  }

  return {
    id: `effective-rate:${pct}`,
    label: `${pct}%`,
    tooltip: `${tooltipBase} — ${stance}`,
    emoji: '📊',
    className,
  };
}

export function tagsFor(finding: FindingOut): Tag[] {
  return [currencyTag(finding), effectiveRateTag(finding)].filter(
    (t): t is Tag => t !== null,
  );
}
